<template>
  <div class="module-selector">
    <div class="selector-header">
      <h3 class="section-title">{{ title }}</h3>
      <p v-if="description" class="section-description">{{ description }}</p>
    </div>

    <div v-if="loading" class="loading-modules">
      <div class="spinner-small"></div>
      <span>載入模組列表中...</span>
    </div>

    <div v-else-if="error" class="modules-error">
      <p class="error-message">{{ error }}</p>
      <button type="button" class="btn btn-sm btn-secondary" @click="handleReload">
        重新載入
      </button>
    </div>

    <div v-else>
      <div class="modules-grid">
        <div
          v-for="module in modules"
          :key="module.id"
          class="module-item"
          :class="{ 'module-selected': modelValue.includes(module.id) }"
        >
          <label class="module-label">
            <input
              type="checkbox"
              :value="module.id"
              :checked="modelValue.includes(module.id)"
              @change="handleToggle(module.id)"
              :disabled="disabledModuleIds.includes(module.id)"
              class="module-checkbox"
            />
            <div class="module-info">
              <div class="module-header">
                <span class="module-icon" v-if="module.icon">
                  <i :class="module.icon"></i>
                </span>
                <span class="module-name">{{ module.moduleName || module.module_name }}</span>
              </div>
              <span v-if="module.description" class="module-description">
                {{ module.description }}
              </span>
              <span class="module-category badge-group">
                <span
                  v-for="type in getModuleVisibleAccountTypes(module)"
                  :key="type"
                  :class="MODULE_ACCOUNT_TYPE_BADGE_CLASS[type]"
                >{{ MODULE_ACCOUNT_TYPE_LABELS[type] }}</span>
                <span v-if="isModuleOptional(module)" class="badge badge-warning">選購</span>
              </span>
            </div>
          </label>
        </div>
      </div>

      <div class="modules-summary">
        已選擇 <strong>{{ modelValue.length }}</strong> 個模組，
        共 <strong>{{ modules.length }}</strong> 個可用模組
      </div>
    </div>
  </div>
</template>

<script>
import {
  getModuleVisibleAccountTypes,
  isModuleOptional,
  MODULE_ACCOUNT_TYPE_LABELS,
  MODULE_ACCOUNT_TYPE_BADGE_CLASS
} from '@/utils/moduleVisibility.js';

export default {
  name: 'ModuleSelector',
  props: {
    title: {
      type: String,
      default: '模組設定'
    },
    description: {
      type: String,
      default: ''
    },
    modelValue: {
      type: Array,
      required: true,
      default: () => []
    },
    modules: {
      type: Array,
      required: true,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    disabledModuleIds: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'reload'],
  setup(props, { emit }) {
    const handleToggle = (moduleId) => {
      if (props.disabledModuleIds.includes(moduleId)) {
        return;
      }

      const newValue = props.modelValue.includes(moduleId)
        ? props.modelValue.filter(id => id !== moduleId)
        : [...props.modelValue, moduleId];
      emit('update:modelValue', newValue);
    };

    const handleReload = () => {
      emit('reload');
    };

    return {
      getModuleVisibleAccountTypes,
      isModuleOptional,
      MODULE_ACCOUNT_TYPE_LABELS,
      MODULE_ACCOUNT_TYPE_BADGE_CLASS,
      handleToggle,
      handleReload
    };
  }
};
</script>

<style scoped>
.module-selector {
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 25px;
}

.selector-header {
  margin-bottom: 20px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  border-bottom: 2px solid #667eea;
  padding-bottom: 8px;
  display: inline-block;
}

.section-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 0;
  line-height: 1.5;
}

.loading-modules,
.modules-error {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;
  background: #f0f0f0;
  border-radius: 6px;
  color: #555;
  font-size: 15px;
  gap: 10px;
}

.spinner-small {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.modules-error {
  flex-direction: column;
}

.error-message {
  color: #dc3545;
  font-weight: 500;
  margin-bottom: 10px;
}

.modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 15px;
  margin-top: 20px;
}

.module-item {
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s;
  cursor: pointer;
  background: white;
}

.module-item:has(.module-checkbox:disabled) {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f9f9f9;
}

.module-item:hover {
  border-color: #667eea;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.module-item:has(.module-checkbox:disabled):hover {
  border-color: #e0e0e0;
  box-shadow: none;
}

.module-item.module-selected {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
}

.module-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  width: 100%;
  margin: 0;
}

.module-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin-top: 2px;
  flex-shrink: 0;
}

.module-checkbox:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.module-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.module-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.module-icon {
  font-size: 20px;
  color: #667eea;
}

.module-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.module-description {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
}

.module-category {
  font-size: 12px;
  color: #999;
  padding: 0;
  background: transparent;
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  width: fit-content;
}

.modules-summary {
  margin-top: 20px;
  padding: 15px;
  background: #e9ecef;
  border-radius: 6px;
  text-align: center;
  font-size: 14px;
  color: #333;
}

.modules-summary strong {
  color: #667eea;
  font-weight: 600;
}
</style>
